import { v4 as uuid } from 'uuid';
import type { ActionItem, KpiMetric, Newsletter, NewsletterSections, NewsletterVersion } from '@/types';
import { getEnabledSections, SECTION_ORDER } from '@/types';
import { versionsStorage } from '@/services/storage';
import { nowIso } from '@/utils/dateUtils';
import { diffText, type DiffPart } from '@/utils/diffUtils';

export type { DiffPart };

export async function listVersions(newsletterId: string): Promise<NewsletterVersion[]> {
  const all = await versionsStorage.getAll();
  return all
    .filter((version) => version.newsletterId === newsletterId)
    .sort((a, b) => b.versionNumber - a.versionNumber);
}

export async function getVersion(id: string): Promise<NewsletterVersion | undefined> {
  return versionsStorage.getById(id);
}

/** Captures a point-in-time snapshot. Called on publish, or on-demand via "Save Version". */
export async function snapshotNewsletter(
  newsletter: Newsletter,
  actionItemIds: string[],
  note?: string,
): Promise<NewsletterVersion> {
  const existing = await listVersions(newsletter.id);
  const nextVersionNumber = (existing[0]?.versionNumber ?? 0) + 1;
  const version: NewsletterVersion = {
    id: uuid(),
    newsletterId: newsletter.id,
    versionNumber: nextVersionNumber,
    note,
    createdAt: nowIso(),
    snapshot: {
      title: newsletter.title,
      sections: newsletter.sections,
      kpis: newsletter.kpis,
      actionItemIds,
      enabledSections: getEnabledSections(newsletter),
    },
  };
  await versionsStorage.save(version);
  return version;
}

export interface SectionDiff {
  section: keyof NewsletterSections;
  changed: boolean;
  parts: DiffPart[];
}

export interface KpiDiffEntry {
  id: string;
  label: string;
  status: 'added' | 'removed' | 'changed' | 'unchanged';
  before?: KpiMetric;
  after?: KpiMetric;
}

export interface ActionItemDiffEntry {
  id: string;
  status: 'added' | 'removed' | 'completed_since' | 'unchanged';
}

export interface VersionComparison {
  sectionDiffs: SectionDiff[];
  kpiDiffs: KpiDiffEntry[];
  titleChanged: boolean;
  titleDiff: DiffPart[];
}

/** Diffs two version snapshots section-by-section and KPI-by-KPI. */
export function compareVersions(base: NewsletterVersion, compare: NewsletterVersion): VersionComparison {
  const sectionDiffs: SectionDiff[] = SECTION_ORDER.map((section) => {
    const before = base.snapshot.sections[section] ?? '';
    const after = compare.snapshot.sections[section] ?? '';
    const parts = diffText(before, after);
    return { section, changed: before !== after, parts };
  });

  const beforeKpis = new Map(base.snapshot.kpis.map((k) => [k.id, k]));
  const afterKpis = new Map(compare.snapshot.kpis.map((k) => [k.id, k]));
  const kpiIds = new Set([...beforeKpis.keys(), ...afterKpis.keys()]);
  const kpiDiffs: KpiDiffEntry[] = Array.from(kpiIds).map((id) => {
    const before = beforeKpis.get(id);
    const after = afterKpis.get(id);
    if (before && !after) return { id, label: before.label, status: 'removed', before };
    if (!before && after) return { id, label: after.label, status: 'added', after };
    if (before && after) {
      const changed = before.value !== after.value || before.target !== after.target;
      return { id, label: after.label, status: changed ? 'changed' : 'unchanged', before, after };
    }
    return { id, label: id, status: 'unchanged' };
  });

  const titleDiff = diffText(base.snapshot.title, compare.snapshot.title);

  return {
    sectionDiffs,
    kpiDiffs,
    titleChanged: base.snapshot.title !== compare.snapshot.title,
    titleDiff,
  };
}

/** Classifies action items between two versions for the diff view (added/removed/completed/unchanged). */
export function diffActionItems(
  baseIds: string[],
  compareIds: string[],
  allItemsById: Map<string, ActionItem>,
): ActionItemDiffEntry[] {
  const baseSet = new Set(baseIds);
  const compareSet = new Set(compareIds);
  const allIds = new Set([...baseSet, ...compareSet]);

  return Array.from(allIds).map((id) => {
    const item = allItemsById.get(id);
    if (baseSet.has(id) && !compareSet.has(id)) return { id, status: 'removed' };
    if (!baseSet.has(id) && compareSet.has(id)) return { id, status: 'added' };
    if (item?.status === 'completed') return { id, status: 'completed_since' };
    return { id, status: 'unchanged' };
  });
}
